<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">B2B 價格設定</h1>
      </div>
      <div class="header-actions">
        <span v-if="saveAllSuccess" class="save-feedback save-feedback--ok">
          <i class="ri-check-line" /> 已儲存
        </span>
        <span v-if="saveAllError" class="save-feedback save-feedback--err">{{ saveAllError }}</span>
        <button type="button" class="btn btn-secondary" @click="goBack">返回商品列表</button>
        <button v-if="canEdit" type="button" class="btn btn-primary" :disabled="savingAll" @click="saveAll">
          <span v-if="savingAll" class="spinner-sm" />
          {{ savingAll ? '儲存中...' : '全部儲存' }}
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="b2b-pricing-alerts">
      <div v-if="loadError" class="alert alert-danger">{{ loadError }}</div>
      <div v-if="tiersError" class="alert alert-warning">{{ tiersError }}</div>
      <div v-if="!canEdit" class="alert alert-info">
        您目前為檢視權限；僅客戶管理員可修改 B2B 價格。
      </div>
    </div>

    <div v-if="loading" class="loading-inline">載入中…</div>

    <div v-else class="b2b-pricing-body">
      <template v-if="product">
        <section class="settings-card b2b-product-meta-card" aria-labelledby="b2b-product-page-name">
          <div class="settings-card-header b2b-product-meta-header">
            <i class="ri-store-2-line variant-card-icon" aria-hidden="true" />
            <div class="b2b-product-meta-header-text">
              <h2 class="settings-card-title">商品資訊</h2>
              <p class="settings-card-desc">商品主檔、圖片與 B2B 設定說明；各會員等級單價請於下方 SKU／規格區填寫（套組為單一計價列時亦同）。</p>
            </div>
            <router-link
              :to="{ name: 'ClientProductEdit', params: { id: product.id } }"
              class="btn btn-outline-secondary btn-sm b2b-product-edit-link"
            >
              <i class="ri-external-link-line" aria-hidden="true" />
              編輯商品
            </router-link>
          </div>
          <div class="b2b-product-meta-body">
            <h1 id="b2b-product-page-name" class="b2b-product-name-heading">
              {{ product.name || '（未命名商品）' }}
            </h1>
            <p class="b2b-product-intro-hint" role="note">
              一般售價 {{ formatCurrency(product.unitPrice) }}；無多規格時為單一 SKU，仍依會員等級設定 B2B 單價。
            </p>

            <div class="b2b-product-gallery-block">
              <h3 class="b2b-subsection-title">
                商品圖片
                <template v-if="displayProductImages.length">（{{ displayProductImages.length }}）</template>
              </h3>
              <ul v-if="displayProductImages.length" class="b2b-product-gallery" role="list">
                <li
                  v-for="(img, idx) in displayProductImages"
                  :key="img.id || img.imageUrl || idx"
                  class="b2b-product-gallery__item"
                >
                  <a
                    :href="img.imageUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="b2b-product-gallery__link"
                    :title="'開啟大圖'"
                  >
                    <img :src="img.imageUrl" :alt="img.altText || '商品圖'" class="b2b-product-gallery__thumb" />
                  </a>
                  <span v-if="img.isPrimary" class="b2b-gallery-badge">主圖</span>
                  <span v-else-if="img.id === '__product_main_image'" class="b2b-gallery-badge b2b-gallery-badge--file">主檔</span>
                </li>
              </ul>
              <p v-else class="b2b-product-gallery-empty text-muted">尚無商品圖片；可至「編輯商品」上傳主圖或相簿。</p>
            </div>

            <dl class="b2b-info-grid">
              <div class="b2b-info-item">
                <dt>商品 ID</dt>
                <dd><code class="b2b-mono">{{ product.id }}</code></dd>
              </div>
              <div class="b2b-info-item">
                <dt>狀態</dt>
                <dd>
                  <span :class="product.status === 'active' ? 'b2b-badge b2b-badge--on' : 'b2b-badge b2b-badge--off'">
                    {{ product.status === 'active' ? '上架' : '下架' }}
                  </span>
                </dd>
              </div>
              <div class="b2b-info-item">
                <dt>類型</dt>
                <dd>{{ productTypeLabel(product.productType) }}</dd>
              </div>
              <div class="b2b-info-item">
                <dt>主 SKU</dt>
                <dd>{{ product.sku || '—' }}</dd>
              </div>
              <div class="b2b-info-item">
                <dt>條碼</dt>
                <dd>{{ product.barcode || '—' }}</dd>
              </div>
              <div class="b2b-info-item">
                <dt>一般售價</dt>
                <dd>{{ formatCurrency(product.unitPrice) }}</dd>
              </div>
              <div class="b2b-info-item b2b-info-item--wide">
                <dt>分類</dt>
                <dd>{{ categoryDisplay(product) }}</dd>
              </div>
              <div class="b2b-info-item">
                <dt>尺寸（長×寬×高 cm）</dt>
                <dd>{{ formatDimensionsCm(product) }}</dd>
              </div>
              <div class="b2b-info-item">
                <dt>重量（kg）</dt>
                <dd>{{ formatNumberOrDash(product.weightKg) }}</dd>
              </div>
              <div class="b2b-info-item">
                <dt>建立時間</dt>
                <dd>{{ formatDateTime(product.createdAt) }}</dd>
              </div>
              <div class="b2b-info-item">
                <dt>更新時間</dt>
                <dd>{{ formatDateTime(product.updatedAt) }}</dd>
              </div>
            </dl>

            <div v-if="product.summary" class="b2b-product-text-block">
              <h3 class="b2b-subsection-title">商品簡述</h3>
              <p class="b2b-product-plain-text">{{ product.summary }}</p>
            </div>

            <div v-if="product.introduction" class="b2b-product-text-block b2b-product-detail-block">
              <h3 class="b2b-subsection-title">內容簡介</h3>
              <div class="b2b-product-detail-html" v-html="product.introduction" />
            </div>
          </div>
        </section>

        <div v-if="!variants.length" class="settings-card commerce-rule-page">
          <p class="empty-hint">
            此商品尚無可計價的 SKU／規格資料。請重新整理頁面，或至商品編輯確認主 SKU 與商品類型；若仍無法顯示請聯繫管理員。
          </p>
        </div>

        <div v-else-if="!tiers.length" class="settings-card commerce-rule-page">
          <p class="empty-hint">尚未設定啟用中的會員等級，請至會員等級管理建立後再回來設定。</p>
        </div>

        <div v-else class="variant-cards">
          <div class="alert alert-info b2b-pricing-semantics" role="note">
            <p class="b2b-pricing-semantics__title">
              <i class="ri-information-line" aria-hidden="true" />
              未填寫的等級代表什麼？
            </p>
            <p>
              B2B 單價為<strong>選填</strong>。若某會員等級欄位留白（或清空後儲存），系統<strong>不會</strong>為該等級建立專屬價；該等級會員在 B2B 結帳／報價時，此 SKU 會使用<strong>一般售價</strong>（即各規格卡上方「售價」金額，對應規格主檔單價）。
            </p>
            <p>
              因此<strong>不必</strong>強制每一等級都填寫：常見用法是僅對特定經銷等級設定特價，其餘等級維持牌價。若您希望每一等級都有獨立 B2B 價，請自行逐格填滿後再儲存。
            </p>
          </div>
          <div v-for="v in variants" :key="v.id" class="settings-card commerce-rule-page variant-card">
            <div class="settings-card-header">
              <i class="ri-price-tag-3-line variant-card-icon" />
              <div class="variant-card-heading">
                <h2 class="settings-card-title">SKU：{{ v.sku || '—' }}</h2>
                <p class="settings-card-desc">
                  售價 {{ formatCurrency(variantListPrice(v)) }}
                  <span v-if="v.isDefaultVariant" class="badge-default-variant">預設規格</span>
                </p>
                <ul class="variant-detail-list" aria-label="規格詳細">
                  <li>
                    <span class="variant-detail-label">規格 ID</span>
                    <code class="b2b-mono b2b-mono--sm">{{ v.id }}</code>
                  </li>
                  <li v-if="formatAttributes(v.attributes)">
                    <span class="variant-detail-label">屬性</span>
                    {{ formatAttributes(v.attributes) }}
                  </li>
                  <li v-if="v.barcode">
                    <span class="variant-detail-label">條碼</span>
                    {{ v.barcode }}
                  </li>
                  <li>
                    <span class="variant-detail-label">庫存</span>
                    {{ formatInteger(v.stockQuantity) }}
                    <span v-if="v.safetyStockQuantity != null" class="variant-detail-muted">
                      （安全庫存 {{ formatInteger(v.safetyStockQuantity) }}）
                    </span>
                  </li>
                  <li>
                    <span class="variant-detail-label">規格狀態</span>
                    <span :class="v.status === 'active' ? 'b2b-badge b2b-badge--on' : 'b2b-badge b2b-badge--off'">
                      {{ v.status === 'active' ? '上架' : '下架' }}
                    </span>
                  </li>
                  <li v-if="variantDimensionsLine(v)">
                    <span class="variant-detail-label">尺寸／重量</span>
                    {{ variantDimensionsLine(v) }}
                  </li>
                  <li v-if="v.createdAt || v.updatedAt">
                    <span class="variant-detail-label">建立／更新</span>
                    <span class="variant-detail-muted">
                      {{ formatDateTime(v.createdAt) }}／{{ formatDateTime(v.updatedAt) }}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <div class="commerce-advanced-body">
              <div class="b2b-tier-table-wrap">
                <table class="b2b-tier-table">
                  <thead>
                    <tr>
                      <th scope="col">會員等級</th>
                      <th scope="col" class="b2b-tier-table__col-code">代碼</th>
                      <th scope="col" class="b2b-tier-table__col-price">B2B 單價</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="t in tiers" :key="t.id">
                      <td class="b2b-tier-table__cell-name">{{ t.tierName || '—' }}</td>
                      <td class="b2b-tier-table__col-code"><code class="tier-code">{{ t.tierCode }}</code></td>
                      <td class="b2b-tier-table__col-price">
                        <input
                          v-model="priceDraft[rowKey(v.id, t.tierCode)]"
                          type="number"
                          step="1"
                          min="0"
                          class="form-control price-input"
                          :disabled="!canEdit"
                          placeholder="未設定"
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { productService } from '@/api/modules/products.js'
import { commerceSettingsApi } from '@/api/modules/commerceSettings.js'
import { memberTierService } from '@/api/modules/memberTiers.js'
import authService from '@/helpers/authservice/authService.js'
import { isClientOwner } from '@/utils/clientAccount.js'
import { usePageTitle } from '@/composables/usePageTitle.js'

usePageTitle('B2B 價格設定')

const route = useRoute()
const router = useRouter()

const productId = computed(() => String(route.params.productId || ''))

const loading = ref(true)
const loadError = ref('')
const tiersError = ref('')
const product = ref(null)
const tiers = ref([])

/** 列 key → 單價字串（與 input 雙向綁定） */
const priceDraft = ref({})
/** 列 key → B2B 價格列 id（有則可刪除） */
const priceRowId = ref({})

const savingKey = ref('')
const savingAll = ref(false)
const saveAllError = ref('')
const saveAllSuccess = ref(false)

const canEdit = computed(() => isClientOwner(authService.getCurrentUser()))

const variants = computed(() => {
  const list = product.value?.variants
  return Array.isArray(list) ? list : []
})

/** 商品層級圖片列表（依 sortOrder） */
const productImagesOrdered = computed(() => {
  const imgs = product.value?.images
  if (!Array.isArray(imgs) || imgs.length === 0) return []
  return [...imgs].sort((a, b) => {
    const sa = Number(a.sortOrder) || 0
    const sb = Number(b.sortOrder) || 0
    if (sa !== sb) return sa - sb
    return String(a.createdAt || '').localeCompare(String(b.createdAt || ''))
  })
})

/**
 * 商品資訊區圖片列：相簿圖片 + 主檔 imageUrl（無相簿列或 URL 未重複時補上主圖）
 */
const displayProductImages = computed(() => {
  const p = product.value
  if (!p) return []
  const rows = productImagesOrdered.value
  const mainUrl = (p.imageUrl && String(p.imageUrl).trim()) || ''
  const rowUrls = new Set(rows.map((r) => r.imageUrl).filter(Boolean))

  if (rows.length === 0) {
    if (!mainUrl) return []
    return [
      {
        id: '__product_main_image',
        imageUrl: mainUrl,
        isPrimary: true,
        altText: p.name ? `${p.name} 主圖` : '商品主圖',
      },
    ]
  }

  if (mainUrl && !rowUrls.has(mainUrl)) {
    return [
      {
        id: '__product_main_image',
        imageUrl: mainUrl,
        isPrimary: false,
        altText: p.name ? `${p.name}（主檔圖）` : '商品主檔圖',
      },
      ...rows,
    ]
  }

  return rows
})

function productTypeLabel(type) {
  if (type === 'bundle') return '套組商品'
  return '一般商品'
}

function categoryDisplay(p) {
  if (!p?.categoryId) return '—'
  const parts = [p.categoryParentName, p.categoryName].filter(Boolean)
  if (parts.length) return parts.join(' › ')
  return p.categoryName || '—'
}

function formatDimensionsCm(row) {
  const l = row?.lengthCm
  const w = row?.widthCm
  const h = row?.heightCm
  if (l == null && w == null && h == null) return '—'
  const fmt = (n) => (n == null || n === '' ? '—' : String(Number(n)))
  return `${fmt(l)} × ${fmt(w)} × ${fmt(h)}`
}

function formatNumberOrDash(n) {
  if (n == null || n === '') return '—'
  return String(Number(n))
}

function formatInteger(n) {
  if (n == null || n === '') return '—'
  return Number(n).toLocaleString('zh-TW')
}

function formatDateTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('zh-TW', { dateStyle: 'short', timeStyle: 'short' })
}

function formatAttributes(attrs) {
  if (!attrs || typeof attrs !== 'object') return ''
  const parts = Object.entries(attrs)
    .filter(([, val]) => val != null && val !== '')
    .map(([k, val]) => `${k}：${val}`)
  return parts.length ? parts.join('；') : ''
}

function variantDimensionsLine(v) {
  const dim = formatDimensionsCm(v)
  const w = formatNumberOrDash(v.weightKg)
  const hasDim = dim !== '—'
  const hasW = w !== '—'
  if (!hasDim && !hasW) return ''
  if (hasDim && hasW) return `${dim}；${w} kg`
  if (hasW) return `${w} kg`
  return dim
}

function rowKey(variantId, tierCode) {
  return `${variantId}:${tierCode}`
}

function formatCurrency(amount) {
  const num = Number(amount)
  if (amount === null || amount === undefined || Number.isNaN(num)) return 'NT$ 0'
  return `NT$ ${Math.round(num).toLocaleString('zh-TW', { maximumFractionDigits: 0 })}`
}

/** 規格列單價；無規格商品的預設列可能未寫入 unitPrice，回退商品主檔售價 */
function variantListPrice(v) {
  const p = v?.unitPrice
  if (p != null && p !== '' && !Number.isNaN(Number(p))) return p
  return product.value?.unitPrice
}

function goBack() {
  router.push({ name: 'ClientCommerceB2bPricing' })
}

function unwrapB2bItems(res) {
  const d = res?.data
  if (d && Array.isArray(d.items)) return d.items
  return []
}

function buildDraftMaps(variantList, tierList, items) {
  const draft = {}
  const ids = {}
  for (const v of variantList) {
    for (const t of tierList) {
      draft[rowKey(v.id, t.tierCode)] = ''
    }
  }
  for (const it of items) {
    const vid = it.variant_id ?? it.variantId
    const tier = it.member_tier ?? it.memberTier
    if (!vid || !tier) continue
    const k = rowKey(vid, tier)
    const up = it.unit_price ?? it.unitPrice
    if (k in draft) {
      draft[k] = up != null && up !== '' ? String(Math.round(Number(up))) : ''
    }
    if (it.id) ids[k] = it.id
  }
  return { draft, ids }
}

async function loadAll() {
  if (!productId.value) {
    loadError.value = '缺少商品 ID'
    loading.value = false
    return
  }
  loading.value = true
  loadError.value = ''
  tiersError.value = ''
  product.value = null
  priceDraft.value = {}
  priceRowId.value = {}
  try {
    const [pres, tres, bres] = await Promise.all([
      productService.getProduct(productId.value),
      memberTierService.getTiers({ isActive: true }),
      commerceSettingsApi.listB2bTierPrices({ productId: productId.value }),
    ])
    if (!pres?.success || !pres.data) {
      loadError.value = pres?.message || '無法載入商品'
      return
    }
    product.value = pres.data
    const tierList = Array.isArray(tres?.data) ? tres.data : []
    if (tres?.success === false) {
      tiersError.value = tres.message || '載入會員等級失敗'
    }
    tiers.value = tierList

    const vlist = Array.isArray(pres.data.variants) ? pres.data.variants : []
    const items = unwrapB2bItems(bres)
    const { draft, ids } = buildDraftMaps(vlist, tierList, items)
    priceDraft.value = draft
    priceRowId.value = ids
  } catch (e) {
    loadError.value = e?.response?.data?.message || e.message || '載入失敗'
  } finally {
    loading.value = false
  }
}

async function saveCell(variantId, tierCode) {
  const k = rowKey(variantId, tierCode)
  const raw = priceDraft.value[k]
  const trimmed = raw === '' || raw == null ? '' : String(raw).trim()
  const existingId = priceRowId.value[k]
  savingKey.value = k
  loadError.value = ''
  try {
    if (trimmed === '') {
      if (existingId) {
        await commerceSettingsApi.deleteB2bTierPrice(existingId)
        const next = { ...priceRowId.value }
        delete next[k]
        priceRowId.value = next
      }
      return
    }
    const n = Math.round(Number(trimmed))
    if (!Number.isFinite(n) || n < 0) {
      loadError.value = '請輸入有效的單價（≥ 0 的整數）'
      return
    }
    const res = await commerceSettingsApi.upsertB2bTierPrice({
      variantId,
      memberTier: tierCode,
      unitPrice: n,
    })
    const body = res?.data
    if (body?.id) {
      priceRowId.value = { ...priceRowId.value, [k]: body.id }
    }
    priceDraft.value = { ...priceDraft.value, [k]: String(n) }
  } catch (e) {
    loadError.value = e?.response?.data?.message || e.message || '儲存失敗'
  } finally {
    savingKey.value = ''
  }
}

async function saveAll() {
  if (!canEdit.value) return
  savingAll.value = true
  saveAllError.value = ''
  saveAllSuccess.value = false
  try {
    for (const k of Object.keys(priceDraft.value)) {
      const colonIdx = k.indexOf(':')
      const variantId = k.slice(0, colonIdx)
      const tierCode = k.slice(colonIdx + 1)
      const raw = priceDraft.value[k]
      const trimmed = raw === '' || raw == null ? '' : String(raw).trim()
      const existingId = priceRowId.value[k]
      if (trimmed === '') {
        if (existingId) {
          await commerceSettingsApi.deleteB2bTierPrice(existingId)
          const next = { ...priceRowId.value }
          delete next[k]
          priceRowId.value = next
        }
        continue
      }
      const n = Math.round(Number(trimmed))
      if (!Number.isFinite(n) || n < 0) {
        saveAllError.value = `無效的單價：${trimmed}`
        return
      }
      const res = await commerceSettingsApi.upsertB2bTierPrice({
        variantId,
        memberTier: tierCode,
        unitPrice: n,
      })
      const body = res?.data
      if (body?.id) {
        priceRowId.value = { ...priceRowId.value, [k]: body.id }
      }
      priceDraft.value = { ...priceDraft.value, [k]: String(n) }
    }
    saveAllSuccess.value = true
    setTimeout(() => { saveAllSuccess.value = false }, 3000)
  } catch (e) {
    saveAllError.value = e?.response?.data?.message || e.message || '儲存失敗'
  } finally {
    savingAll.value = false
  }
}

onMounted(loadAll)
watch(productId, () => {
  loadAll()
})
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left { display: flex; align-items: center; }
.header-actions { display: flex; align-items: center; gap: 8px; }

.page-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.page-container {
  padding: 20px;
  padding-top: 72px;
  max-width: 1080px;
}

.btn { padding: 6px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 6px; }
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-secondary:hover:not(:disabled) { background: #e5e7eb; }

.save-feedback {
  font-size: 13px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.save-feedback--ok { color: #059669; }
.save-feedback--err { color: #dc2626; }

.spinner-sm {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: b2b-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes b2b-spin { to { transform: rotate(360deg); } }

/* 與運費規則等頁一致：settings-card 定義在別頁 scoped 內，此頁自帶卡片樣式 */
.b2b-pricing-body {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.b2b-pricing-alerts {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
}

.b2b-pricing-alerts .alert {
  margin: 0;
}

.loading-inline {
  padding: 40px 24px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
}

.settings-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
}

.settings-card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px 24px;
  background: #f8f9ff;
  border-bottom: 1px solid #e9ecef;
}

.settings-card-header > i {
  font-size: 28px;
  margin-top: 2px;
  flex-shrink: 0;
}

.variant-card-icon {
  color: #667eea;
}

.settings-card-title {
  font-size: 17px;
  font-weight: 600;
  margin: 0 0 4px;
  color: #2d3748;
}

.settings-card-desc {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
  line-height: 1.5;
}

.variant-card-heading {
  min-width: 0;
}

.commerce-rule-page {
  margin-bottom: 0;
}

.variant-cards {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.b2b-pricing-semantics {
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
}

.b2b-pricing-semantics p {
  margin: 0 0 10px;
}

.b2b-pricing-semantics p:last-child {
  margin-bottom: 0;
}

.b2b-pricing-semantics__title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  margin-bottom: 8px !important;
}

.variant-card .settings-card-header {
  align-items: flex-start;
}

.commerce-advanced-body {
  padding: 20px 0 20px;
}

.b2b-tier-table-wrap {
  margin: 0 24px;
  overflow-x: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fff;
  -webkit-overflow-scrolling: touch;
}

.b2b-tier-table {
  width: 100%;
  min-width: 520px;
  border-collapse: collapse;
  font-size: 13px;
}

.b2b-tier-table th,
.b2b-tier-table td {
  padding: 12px 14px;
  text-align: left;
  vertical-align: middle;
  border-bottom: 1px solid #eef0f3;
}

.b2b-tier-table th {
  background: #eef1f5;
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
}

.b2b-tier-table tbody tr:last-child td {
  border-bottom: none;
}

.b2b-tier-table__cell-name {
  font-weight: 500;
  color: #334155;
}

.b2b-tier-table__col-code {
  width: 1%;
}

.b2b-tier-table__col-price {
  width: 1%;
}

.b2b-tier-table__col-action {
  width: 1%;
  text-align: right;
}

.tier-code {
  font-size: 12px;
  padding: 2px 8px;
  background: #f1f5f9;
  border-radius: 4px;
  color: #475569;
}

.price-input {
  width: 100%;
  max-width: 160px;
  min-width: 100px;
}

.btn-sm {
  font-size: 12px;
  padding: 6px 12px;
  white-space: nowrap;
}

.empty-hint {
  padding: 24px;
  margin: 0;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.55;
}

.badge-default-variant {
  display: inline-block;
  margin-left: 8px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 500;
  color: #4c51bf;
  background: #ebebff;
  border-radius: 999px;
  vertical-align: middle;
}

.alert {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
}

.alert-warning {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fde68a;
}

.alert-info {
  background: #eff6ff;
  color: #1e40af;
  border: 1px solid #bfdbfe;
}

.b2b-product-meta-header {
  flex-wrap: wrap;
  align-items: flex-start;
}

.b2b-product-meta-header-text {
  flex: 1;
  min-width: 160px;
}

.b2b-product-edit-link {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.b2b-product-meta-body {
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.b2b-product-name-heading {
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.35;
  color: #1e293b;
  word-break: break-word;
}

.b2b-product-intro-hint {
  margin: 0;
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.55;
  color: #1e3a5f;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
}

.b2b-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 14px 20px;
  margin: 0;
}

.b2b-info-item {
  margin: 0;
}

.b2b-info-item--wide {
  grid-column: 1 / -1;
}

.b2b-info-item dt {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: none;
  letter-spacing: normal;
}

.b2b-info-item dd {
  margin: 0;
  font-size: 14px;
  color: #1e293b;
  line-height: 1.45;
  word-break: break-word;
}

.b2b-mono {
  font-size: 12px;
  padding: 2px 8px;
  background: #f1f5f9;
  border-radius: 4px;
  color: #334155;
}

.b2b-mono--sm {
  font-size: 11px;
}

.b2b-badge {
  display: inline-block;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 999px;
}

.b2b-badge--on {
  color: #166534;
  background: #dcfce7;
}

.b2b-badge--off {
  color: #57534e;
  background: #f5f5f4;
}

.b2b-subsection-title {
  margin: 0 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.b2b-product-gallery-block {
  margin: 0;
}

.b2b-product-gallery {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.b2b-product-gallery__item {
  position: relative;
  margin: 0;
  padding: 0;
}

.b2b-product-gallery__link {
  display: block;
  line-height: 0;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e9ecef;
  transition: box-shadow 0.15s ease;
}

.b2b-product-gallery__link:hover {
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
}

.b2b-product-gallery__thumb {
  width: 104px;
  height: 104px;
  object-fit: cover;
  display: block;
  background: #f8f9fa;
}

.b2b-product-gallery-empty {
  margin: 0;
  font-size: 13px;
  line-height: 1.5;
}

.b2b-gallery-badge {
  position: absolute;
  bottom: 6px;
  left: 6px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  color: #fff;
  background: rgba(102, 126, 234, 0.92);
  border-radius: 4px;
}

.b2b-gallery-badge--file {
  background: rgba(71, 85, 105, 0.92);
}

.b2b-product-text-block {
  margin: 0;
}

.b2b-product-plain-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #334155;
  white-space: pre-wrap;
}

.b2b-product-detail-block {
  border-top: 1px solid #eef0f3;
  padding-top: 20px;
}

.b2b-product-detail-html {
  font-size: 14px;
  line-height: 1.6;
  color: #334155;
  max-height: 480px;
  overflow: auto;
  padding: 12px 14px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  background: #fafafa;
}

.b2b-product-detail-html :deep(p) {
  margin: 0 0 0.75em;
}

.b2b-product-detail-html :deep(p:last-child) {
  margin-bottom: 0;
}

.b2b-product-detail-html :deep(ul),
.b2b-product-detail-html :deep(ol) {
  margin: 0 0 0.75em;
  padding-left: 1.25em;
}

.b2b-product-detail-html :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
}

.b2b-product-detail-html :deep(a) {
  color: #4f46e5;
}

.variant-detail-list {
  list-style: none;
  margin: 10px 0 0;
  padding: 0;
  font-size: 13px;
  line-height: 1.55;
  color: #475569;
}

.variant-detail-list li {
  margin: 0 0 6px;
  padding: 0;
}

.variant-detail-list li:last-child {
  margin-bottom: 0;
}

.variant-detail-label {
  display: inline-block;
  min-width: 4.5em;
  margin-right: 6px;
  font-weight: 600;
  color: #64748b;
}

.variant-detail-muted {
  color: #94a3b8;
  font-size: 12px;
}
</style>
